// "Ziua mea" — Food Logger tab
// 4-level input: search → unit → category → quick add.
// Zero AI, pure math, uses /api/log/* endpoints.
import React, { useEffect, useState, useCallback } from "react";
import { useI18n } from "../../i18n";
import api from "../../api";

const COLOR_MAP = {
  green: "#22c55e",
  yellow: "#eab308",
  orange: "#f97316",
  red: "#ef4444",
};

const ProgressBar = ({ label, data, unit = "kcal" }) => {
  if (!data) return null;
  const pct = Math.min(data.pct, 130);
  return (
    <div className="mb-3" data-testid={`budget-bar-${label.toLowerCase()}`}>
      <div className="flex justify-between text-xs font-mono tracking-wider mb-1">
        <span className="text-white/70">{label}</span>
        <span style={{ color: COLOR_MAP[data.color] }}>
          {data.consumed} / {data.target} {unit} ({data.pct}%)
        </span>
      </div>
      <div className="h-2 bg-white/5 relative overflow-hidden" style={{ border: "1px solid #27272A" }}>
        <div
          className="h-full transition-all duration-300"
          style={{
            width: `${Math.min(pct, 100)}%`,
            background: COLOR_MAP[data.color],
          }}
        />
        {data.pct > 100 && (
          <div
            className="absolute top-0 h-full opacity-60"
            style={{
              left: "100%",
              width: `${Math.min(data.pct - 100, 30)}%`,
              background: COLOR_MAP.red,
            }}
          />
        )}
      </div>
      <div className="text-xs text-white/40 mt-1 font-mono">
        {data.remaining >= 0
          ? `Rămân ${data.remaining} ${unit}`
          : `Depășit cu ${Math.abs(data.remaining)} ${unit}`}
      </div>
    </div>
  );
};

// Pre-loaded sample entries for DEMO mode — shows the magic without auth.
const DEMO_SAMPLE_ENTRIES = [
  { id: "demo1", type: "unit", food_key: "mici", label_ro: "Mici (1 buc ≈ 60g)", label_en: "Romanian Grilled Sausage (mic)",
    grams: 240, kcal: 624, protein: 43.2, carbs: 2.4, fat: 50.4, quantity: 4, unit: "mici", added_at: "" },
  { id: "demo2", type: "food", food_key: "french_fries", label_ro: "Cartofi prăjiți", label_en: "French Fries",
    grams: 200, kcal: 624, protein: 6.8, carbs: 82, fat: 30, added_at: "" },
  { id: "demo3", type: "food", food_key: "beer", label_ro: "Bere", label_en: "Beer (regular)",
    grams: 500, kcal: 215, protein: 2.5, carbs: 18, fat: 0, added_at: "" },
];

const FoodLog = ({ profile, demoMode = false }) => {
  const { lang } = useI18n();
  const isLifestyle = profile?.profile_type === "lifestyle" || profile?.sport === "lifestyle";
  const [today, setToday] = useState(null);
  const [catalog, setCatalog] = useState(null);
  const [query, setQuery] = useState("");
  const [searchResults, setSearchResults] = useState([]);
  const [searching, setSearching] = useState(false);
  const [pickedFood, setPickedFood] = useState(null);
  const [pickedGrams, setPickedGrams] = useState(150);
  const [showCategory, setShowCategory] = useState(false);
  const [pickedCategory, setPickedCategory] = useState(null);
  const [pickedCatGrams, setPickedCatGrams] = useState(200);
  const [suggestion, setSuggestion] = useState(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  // Day-type drives the live budget: rest day burns less than match day.
  // Lifestyle profiles ignore this (every day = standard).
  const [dayType, setDayType] = useState(isLifestyle ? "standard" : "hard_training");

  const refresh = useCallback(async () => {
    try {
      if (demoMode) {
        // Compute locally — no auth, no API
        const r = await api.post("/log/today-public", { profile, entries: today?.entries || DEMO_SAMPLE_ENTRIES, day_type: dayType });
        setToday(r.data);
        return;
      }
      const r = await api.post("/log/today", { profile, day_type: dayType });
      setToday(r.data);
    } catch (e) {
      setError(e?.response?.data?.detail || "Eroare la încărcare log");
    }
  }, [profile, demoMode, today?.entries, dayType]);

  useEffect(() => {
    api.get("/log/catalog").then((r) => setCatalog(r.data));
    if (demoMode) {
      // Seed demo with the "ate badly" sample
      api.post("/log/today-public", { profile, entries: DEMO_SAMPLE_ENTRIES, day_type: dayType })
        .then((r) => setToday(r.data));
    } else {
      refresh();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [demoMode]);

  // When the user changes day_type, recompute the budget against the same entries.
  useEffect(() => {
    if (!today) return;
    refresh();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dayType]);

  // Debounced search
  useEffect(() => {
    if (!query || query.length < 2) {
      setSearchResults([]);
      return;
    }
    const t = setTimeout(async () => {
      setSearching(true);
      try {
        const r = await api.post("/log/search", { q: query });
        setSearchResults(r.data.results);
      } finally {
        setSearching(false);
      }
    }, 250);
    return () => clearTimeout(t);
  }, [query]);

  const buildLocalEntry = (payload) => {
    // Mirror backend's entry_from_food/category/quick logic for demo mode.
    const id = "d" + Math.random().toString(36).slice(2, 8);
    if (payload.type === "quick" && catalog) {
      const q = catalog.quick_meals.find((m) => m.key === payload.food_key);
      if (!q) return null;
      return { id, type: "quick", food_key: q.key, label_ro: q.label_ro, label_en: q.label_en,
        grams: null, kcal: q.kcal, protein: q.protein, carbs: q.carbs, fat: q.fat, added_at: "" };
    }
    if (payload.type === "food" && pickedFood) {
      const f = pickedFood;
      const g = payload.grams / 100;
      return { id, type: "food", food_key: f.key, label_ro: f.label_ro, label_en: f.label_en,
        grams: payload.grams,
        kcal: Math.round(f.kcal_per_100g * g * 10) / 10,
        protein: Math.round(f.protein_per_100g * g * 10) / 10,
        carbs: Math.round(f.carbs_per_100g * g * 10) / 10,
        fat: Math.round(f.fat_per_100g * g * 10) / 10,
        added_at: "" };
    }
    if (payload.type === "category" && catalog) {
      const c = catalog.generic_categories.find((x) => x.key === payload.food_key);
      if (!c) return null;
      const g = payload.grams / 100;
      return { id, type: "category", food_key: c.key, label_ro: c.label_ro, label_en: c.label_en,
        grams: payload.grams,
        kcal: Math.round(c.kcal * g * 10) / 10,
        protein: Math.round(c.protein * g * 10) / 10,
        carbs: Math.round(c.carbs * g * 10) / 10,
        fat: Math.round(c.fat * g * 10) / 10,
        added_at: "" };
    }
    return null;
  };

  const addEntry = async (payload) => {
    setBusy(true);
    setError("");
    try {
      if (demoMode) {
        const newEntry = buildLocalEntry(payload);
        if (!newEntry) { setBusy(false); return; }
        const newEntries = [...(today?.entries || []), newEntry];
        const r = await api.post("/log/today-public", { profile, entries: newEntries, day_type: dayType });
        setToday(r.data);
      } else {
        await api.post("/log/entry", { profile, ...payload });
        await refresh();
      }
      setSuggestion(null);
      setPickedFood(null);
      setPickedCategory(null);
      setQuery("");
      setSearchResults([]);
    } catch (e) {
      setError(e?.response?.data?.detail || "Eroare la adăugare");
    } finally {
      setBusy(false);
    }
  };

  const deleteEntry = async (entryId) => {
    setBusy(true);
    try {
      if (demoMode) {
        const newEntries = (today?.entries || []).filter((e) => e.id !== entryId);
        const r = await api.post("/log/today-public", { profile, entries: newEntries, day_type: dayType });
        setToday(r.data);
      } else {
        await api.post("/log/delete", { profile, entry_id: entryId });
        await refresh();
      }
      setSuggestion(null);
    } finally {
      setBusy(false);
    }
  };

  const clearDay = async () => {
    if (!window.confirm(lang === "ro" ? "Sigur ștergi toate intrările de azi?" : "Clear all today's entries?")) return;
    setBusy(true);
    try {
      if (demoMode) {
        const r = await api.post("/log/today-public", { profile, entries: [], day_type: dayType });
        setToday(r.data);
      } else {
        await api.post("/log/clear", { profile });
        await refresh();
      }
      setSuggestion(null);
    } finally {
      setBusy(false);
    }
  };

  const getSuggestion = async () => {
    setBusy(true);
    setError("");
    try {
      const r = demoMode
        ? await api.post("/log/suggest-public", { profile, entries: today?.entries || [], day_type: dayType })
        : await api.post("/log/suggest", { profile, day_type: dayType });
      setSuggestion(r.data);
    } catch (e) {
      setError(e?.response?.data?.detail || "Eroare la sugestii");
    } finally {
      setBusy(false);
    }
  };

  if (!today) {
    return <div className="text-white/60 font-mono text-sm">Încarc...</div>;
  }

  const T = {
    ro: {
      eyebrow: "// ZIUA MEA",
      title: "Ce ai mâncat azi?",
      midnightWarn: "⚠ Atenție: ziua se închide la 00:00 fix. Tot ce mănânci după miezul nopții se contabilizează pe ziua de mâine. Metabolismul nu doarme — fii cinstit cu tine.",
      disclaimer: "Valorile pentru mese neplanificate sunt estimative (±10-15%). Direcția contează mai mult decât precizia la gram. Scopul: să știi dacă ziua merge bine sau prost — nu să cântărești firul de praf de pe farfurie.",
      quickAdd: "Adăugare rapidă",
      search: "Caută aliment",
      searchPlaceholder: "ex: ceafă, mici, pizza, kürtős...",
      noResults: "Nimic găsit. Încearcă o categorie generică ↓",
      portion: "Cantitate (g)",
      add: "+ Adaugă",
      genericCats: "Nu găsești? Alege categoria",
      mealsLogged: "Ce ai mâncat azi",
      empty: "Nu ai adăugat nimic încă. Începe cu un buton rapid sau caută alimentul.",
      remove: "✕",
      clear: "Șterge tot",
      budgetTitle: "Bugetul tău de azi",
      suggestBtn: "Cum îmi închid ziua optim?",
      suggestTitle: "Cina ta optimă pentru azi",
      statusGood: "Zi excelentă",
      statusOk: "Zi bună",
      statusBad: "Zi grea",
      kcal: "kcal",
      protein: "Proteine",
      carbs: "Carbohidrați",
      fat: "Grăsimi",
    },
    en: {
      eyebrow: "// MY DAY",
      title: "What did you eat today?",
      midnightWarn: "⚠ Heads-up: the day closes at 00:00 sharp. Anything you eat past midnight counts for tomorrow. Metabolism doesn't sleep — be honest.",
      disclaimer: "Unplanned-meal values are estimates (±10-15%). Direction matters more than gram-perfect accuracy. The goal: know if your day went well or poorly — not to weigh dust on the plate.",
      quickAdd: "Quick Add",
      search: "Search food",
      searchPlaceholder: "ex: chicken, pizza, donut...",
      noResults: "Nothing found. Try a generic category ↓",
      portion: "Amount (g)",
      add: "+ Add",
      genericCats: "Can't find it? Pick a category",
      mealsLogged: "What you've eaten today",
      empty: "Nothing logged yet. Use a quick button or search.",
      remove: "✕",
      clear: "Clear all",
      budgetTitle: "Today's budget",
      suggestBtn: "How do I close my day optimally?",
      suggestTitle: "Your optimal closing meals",
      statusGood: "Excellent day",
      statusOk: "Good day",
      statusBad: "Tough day",
      kcal: "kcal",
      protein: "Protein",
      carbs: "Carbs",
      fat: "Fat",
    },
  };
  const t = T[lang] || T.ro;

  return (
    <div className="space-y-8" data-testid="food-log-panel">
      <div>
        <div className="font-mono text-xs tracking-widest text-[#FF3B30]">{t.eyebrow}</div>
        <h2 className="font-heading text-3xl md:text-4xl mt-1">{t.title}</h2>
      </div>

      {/* Day-type selector — drives the live budget (rest day = lower kcal target).
          Hidden for lifestyle profiles where every day is "standard". */}
      {!isLifestyle && (
        <div className="sne-card" data-testid="foodlog-daytype-row">
          <div className="font-mono text-xs tracking-widest text-white/50 mb-2">
            {lang === "ro" ? "Tipul zilei (influențează bugetul de kcal)" : "Day type (drives kcal budget)"}
          </div>
          <div className="flex flex-wrap gap-2">
            {["hard_training", "light_training", "double_session", "match", "rest"].map((dt) => (
              <button
                key={dt}
                onClick={() => setDayType(dt)}
                data-testid={`foodlog-daytype-${dt}`}
                className="font-mono text-xs tracking-wider px-3 py-2"
                style={{
                  background: dayType === dt ? "#FF3B30" : "transparent",
                  color: dayType === dt ? "white" : "#cccccc",
                  border: `1px solid ${dayType === dt ? "#FF3B30" : "#27272A"}`,
                  cursor: "pointer",
                }}
              >
                {lang === "ro"
                  ? ({hard_training: "Antrenament Greu", light_training: "Antrenament Ușor", double_session: "Sesiune Dublă", match: "Meci", rest: "Odihnă"})[dt]
                  : ({hard_training: "Hard Training", light_training: "Light Training", double_session: "Double Session", match: "Match", rest: "Rest"})[dt]}
              </button>
            ))}
          </div>
        </div>
      )}

      {/* Always-visible midnight rollover policy notice (per user request) */}
      <div
        className="font-mono text-xs tracking-wider px-4 py-3"
        style={{ background: "rgba(234,179,8,0.08)", border: "1px solid #eab308", color: "#eab308" }}
        data-testid="midnight-warning"
      >
        {t.midnightWarn}
      </div>

      {/* LEVEL 4 — Quick Add (top, biggest impact) */}
      {catalog && (
        <div data-testid="quick-add-section">
          <div className="font-mono text-xs tracking-widest text-white/50 mb-3">{t.quickAdd}</div>
          <div className="flex flex-wrap gap-2">
            {catalog.quick_meals.map((q) => (
              <button
                key={q.key}
                disabled={busy}
                onClick={() => addEntry({ type: "quick", food_key: q.key })}
                data-testid={`quick-${q.key}`}
                className="px-3 py-2 font-mono text-xs tracking-wider hover:bg-white/5 transition-colors"
                style={{ border: "1px solid #27272A", background: "#0E0E10" }}
              >
                <span className="text-lg mr-2">{q.icon}</span>
                <span>{lang === "ro" ? q.label_ro : q.label_en}</span>
                <span className="text-white/40 ml-2">+{q.kcal} kcal</span>
              </button>
            ))}
          </div>
        </div>
      )}

      {/* LEVEL 1 — Search */}
      <div data-testid="search-section">
        <div className="font-mono text-xs tracking-widest text-white/50 mb-3">{t.search}</div>
        <input
          type="text"
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          placeholder={t.searchPlaceholder}
          className="sne-input w-full"
          data-testid="food-search-input"
        />
        {searching && <div className="text-xs text-white/40 mt-1">...</div>}
        {searchResults.length > 0 && !pickedFood && (
          <div className="mt-2 max-h-64 overflow-y-auto" style={{ border: "1px solid #27272A" }}>
            {searchResults.map((r) => (
              <button
                key={r.key}
                onClick={() => setPickedFood(r)}
                className="block w-full text-left px-3 py-2 hover:bg-white/5 text-sm"
                data-testid={`search-result-${r.key}`}
              >
                <span className="text-white">{lang === "ro" ? r.label_ro : r.label_en}</span>
                <span className="text-white/40 text-xs ml-2 font-mono">
                  {r.kcal_per_100g} kcal · P {r.protein_per_100g} · C {r.carbs_per_100g} · F {r.fat_per_100g} / 100g
                </span>
              </button>
            ))}
          </div>
        )}
        {pickedFood && (
          <div className="mt-3 p-3 flex items-end gap-3" style={{ border: "1px solid #FF3B30", background: "#0E0E10" }}>
            <div className="flex-1">
              <div className="font-mono text-xs text-[#FF3B30]">
                {lang === "ro" ? pickedFood.label_ro : pickedFood.label_en}
              </div>
              <div className="text-xs text-white/50 mt-1 font-mono">
                {Math.round(pickedFood.kcal_per_100g * pickedGrams / 100)} kcal ·
                P {Math.round(pickedFood.protein_per_100g * pickedGrams / 100 * 10) / 10}g ·
                C {Math.round(pickedFood.carbs_per_100g * pickedGrams / 100 * 10) / 10}g ·
                F {Math.round(pickedFood.fat_per_100g * pickedGrams / 100 * 10) / 10}g
              </div>
            </div>
            <input
              type="number"
              value={pickedGrams || ""}
              onFocus={(e) => e.target.select()}
              onChange={(e) => setPickedGrams(parseFloat(e.target.value) || 0)}
              min="5"
              step="10"
              className="sne-input w-24"
              data-testid="food-grams-input"
            />
            <button
              onClick={() => addEntry({ type: "food", food_key: pickedFood.key, grams: pickedGrams })}
              disabled={busy || pickedGrams < 5}
              className="btn-primary"
              data-testid="add-food-btn"
            >
              {t.add}
            </button>
            <button onClick={() => setPickedFood(null)} className="text-white/40 px-2">✕</button>
          </div>
        )}
        {query.length >= 2 && !searching && searchResults.length === 0 && !pickedFood && (
          <div className="mt-2 text-xs text-white/50">{t.noResults}</div>
        )}
      </div>

      {/* LEVEL 3 — Generic categories */}
      <div data-testid="category-section">
        <div className="font-mono text-xs tracking-widest text-white/50 mb-3">{t.genericCats}</div>
        {catalog && (
          <div className="grid grid-cols-2 md:grid-cols-3 gap-2">
            {catalog.generic_categories.map((c) => (
              <button
                key={c.key}
                onClick={() => setPickedCategory(c)}
                className="text-left px-3 py-2 hover:bg-white/5"
                style={{ border: "1px solid #27272A", background: "#0E0E10" }}
                data-testid={`category-${c.key}`}
              >
                <div className="font-mono text-xs">
                  <span className="text-lg mr-2">{c.icon}</span>
                  {lang === "ro" ? c.label_ro : c.label_en}
                </div>
                <div className="text-white/40 text-[10px] mt-1 font-mono">
                  ~{c.kcal} kcal/100g
                </div>
              </button>
            ))}
          </div>
        )}
        {pickedCategory && (
          <div className="mt-3 p-3 flex items-end gap-3" style={{ border: "1px solid #FF3B30", background: "#0E0E10" }}>
            <div className="flex-1">
              <div className="font-mono text-xs text-[#FF3B30]">
                {pickedCategory.icon} {lang === "ro" ? pickedCategory.label_ro : pickedCategory.label_en}
              </div>
              <div className="text-xs text-white/50 mt-1 font-mono">
                {Math.round(pickedCategory.kcal * pickedCatGrams / 100)} kcal estimat
              </div>
            </div>
            <input
              type="number"
              value={pickedCatGrams || ""}
              onFocus={(e) => e.target.select()}
              onChange={(e) => setPickedCatGrams(parseFloat(e.target.value) || 0)}
              min="10"
              step="50"
              className="sne-input w-24"
              data-testid="category-grams-input"
            />
            <button
              onClick={() => addEntry({ type: "category", food_key: pickedCategory.key, grams: pickedCatGrams })}
              disabled={busy}
              className="btn-primary"
              data-testid="add-category-btn"
            >
              {t.add}
            </button>
            <button onClick={() => setPickedCategory(null)} className="text-white/40 px-2">✕</button>
          </div>
        )}
      </div>

      {/* Logged entries */}
      <div data-testid="log-entries-section">
        <div className="flex items-center justify-between mb-3">
          <div className="font-mono text-xs tracking-widest text-white/50">{t.mealsLogged}</div>
          {today.entries.length > 0 && (
            <button onClick={clearDay} className="text-xs text-white/40 hover:text-red-400 font-mono" data-testid="clear-all-btn">
              {t.clear} ✕
            </button>
          )}
        </div>
        {today.entries.length === 0 ? (
          <div className="text-sm text-white/40 italic" data-testid="log-empty">{t.empty}</div>
        ) : (
          <div className="divide-y" style={{ borderColor: "#27272A" }}>
            {today.entries.map((e) => (
              <div key={e.id} className="flex items-center justify-between py-2" data-testid={`entry-${e.id}`}>
                <div className="flex-1">
                  <div className="text-sm text-white">{lang === "ro" ? e.label_ro : e.label_en}</div>
                  <div className="text-xs text-white/40 font-mono">
                    {e.grams ? `${e.grams}g · ` : ""}
                    {e.kcal} kcal · P {e.protein}g · C {e.carbs}g · F {e.fat}g
                    {e.unit && <span className="ml-2 text-amber-400">({e.quantity}× {e.unit})</span>}
                  </div>
                </div>
                <button
                  onClick={() => deleteEntry(e.id)}
                  className="text-white/30 hover:text-red-400 px-2"
                  data-testid={`delete-${e.id}`}
                >
                  ✕
                </button>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Budget bars */}
      <div className="p-4" style={{ border: "1px solid #27272A", background: "#0E0E10" }} data-testid="budget-section">
        <div className="font-mono text-xs tracking-widest text-white/70 mb-4">{t.budgetTitle}</div>
        <ProgressBar label={t.kcal} data={today.budget.kcal} unit={t.kcal} />
        <ProgressBar label={t.protein} data={today.budget.protein} unit="g" />
        <ProgressBar label={t.carbs} data={today.budget.carbs} unit="g" />
        <ProgressBar label={t.fat} data={today.budget.fat} unit="g" />
      </div>

      {/* Suggest button */}
      {today.entries.length > 0 && (
        <button
          onClick={getSuggestion}
          disabled={busy}
          className="btn-primary w-full"
          data-testid="suggest-btn"
        >
          {busy ? "..." : `→ ${t.suggestBtn}`}
        </button>
      )}

      {/* Suggestion result — flat food list, athlete picks/combines freely */}
      {suggestion && (
        <div className="p-4" style={{ border: "1px solid #FF3B30", background: "#0E0E10" }} data-testid="suggestion-result">
          <div className="font-mono text-xs tracking-widest text-[#FF3B30] mb-2">{t.suggestTitle}</div>

          {/* Overshoot warning — red box, only when any macro exceeded */}
          {suggestion.any_overshoot && (
            <div className="mb-3 p-3" style={{ background: "#1F1010", border: "1px solid #FF3B30" }} data-testid="overshoot-warning">
              <div className="font-mono text-[10px] tracking-widest text-[#FF3B30] mb-1">⚠ {lang === "ro" ? "ZI RATATĂ SPORTIV" : "DAY MISSED"}</div>
              <div className="text-sm text-white/90">{lang === "ro" ? suggestion.warning_ro : suggestion.warning_en}</div>
            </div>
          )}

          {/* Headline / instruction */}
          {!suggestion.any_overshoot && (
            <div className="text-sm text-white/80 mb-4">
              {lang === "ro" ? suggestion.headline_ro : suggestion.headline_en}
            </div>
          )}

          {/* Flat food list — choose what & when */}
          {suggestion.items && suggestion.items.length > 0 && (
            <div className="space-y-2 mt-2">
              {suggestion.items.map((it, idx) => (
                <div
                  key={idx}
                  className="flex items-center justify-between px-3 py-2"
                  style={{
                    background: it.skip ? "#1F1010" : "#141416",
                    border: `1px solid ${it.skip ? "#FF3B3040" : "#27272A"}`,
                  }}
                  data-testid={`suggestion-item-${idx}`}
                >
                  <div className="flex-1 min-w-0">
                    <div className={`text-sm ${it.skip ? "text-[#FF8080] italic" : "text-white"}`}>
                      {lang === "ro" ? it.label_ro : it.label_en}
                    </div>
                    {!it.skip && (
                      <div className="font-mono text-[10px] tracking-wider text-amber-400 mt-1">
                        {lang === "ro" ? it.portion_ro : it.portion_en}
                      </div>
                    )}
                  </div>
                  {!it.skip && (
                    <div className="font-mono text-[11px] tracking-wider text-white/60 text-right ml-3 whitespace-nowrap">
                      {it.kcal} kcal
                      <div className="text-[10px] text-white/40">
                        P{it.protein} C{it.carbs} F{it.fat}
                      </div>
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}

          {/* Tomorrow tip — yellow box */}
          {suggestion.tomorrow_tip_ro && (
            <div className="mt-4 p-3" style={{ background: "#1F1A0A", border: "1px solid #F59E0B" }} data-testid="tomorrow-tip">
              <div className="font-mono text-[10px] tracking-widest text-amber-400 mb-1">→ {lang === "ro" ? "PENTRU MÂINE" : "FOR TOMORROW"}</div>
              <div className="text-sm text-white/90">
                {lang === "ro" ? suggestion.tomorrow_tip_ro : suggestion.tomorrow_tip_en}
              </div>
            </div>
          )}
        </div>
      )}

      {error && <div className="text-red-400 text-sm" data-testid="log-error">{String(error)}</div>}

      {/* Disclaimer */}
      <div className="text-xs text-white/40 italic font-mono leading-relaxed pt-4" data-testid="log-disclaimer">
        {t.disclaimer}
      </div>
    </div>
  );
};

export default FoodLog;
